/**
 * Admin Page - User management for administrators
 *
 * Allows admins to view, edit roles, and manage users.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  adminService,
  type UserSummary,
  type WhitelistResponse,
  type ClinicGroupSummary,
} from "../services/admin";
import { authService } from "../services/auth";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/card/card.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/checkbox/checkbox.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/select/select.js";
import "@awesome.me/webawesome/dist/components/option/option.js";
import "@awesome.me/webawesome/dist/components/tab-group/tab-group.js";
import "@awesome.me/webawesome/dist/components/tab/tab.js";
import "@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js";
import "@awesome.me/webawesome/dist/components/switch/switch.js";
import "../components/app-shell";

@customElement("admin-page")
export class AdminPage extends LitElement {
  @state() private users: UserSummary[] = [];
  @state() private isLoading = true;
  @state() private error = "";
  @state() private editingUser: UserSummary | null = null;
  @state() private editRoles: Set<string> = new Set();
  @state() private showDeletedUsers = false;
  @state() private searchQuery = "";
  @state() private roleFilter = "all";
  @state() private activeTab = "users";

  // Whitelist state
  @state() private whitelist: WhitelistResponse | null = null;
  @state() private whitelistLoading = false;
  @state() private newWhitelistEmail = "";
  @state() private newWhitelistNote = "";
  @state() private newWhitelistClinicGroupId = "";
  @state() private newWhitelistRole = "";

  // Clinic Groups state
  @state() private clinicGroups: ClinicGroupSummary[] = [];
  @state() private clinicsLoading = false;
  @state() private newClinicName = "";
  @state() private newClinicDescription = "";

  static styles = css`
    :host {
      display: block;
    }

    .admin-content {
      padding: 1rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.5rem;
      color: #333;
    }

    .subtitle {
      color: #6c757d;
      font-size: 0.875rem;
      margin-bottom: 1.5rem;
    }

    .toolbar {
      display: flex;
      gap: 1rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
      align-items: center;
    }

    .search-box {
      flex: 1;
      min-width: 200px;
    }
    .role-filter {
      min-width: 150px;
    }

    .tab-content {
      padding: 1rem 0;
    }

    .whitelist-section {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .whitelist-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .whitelist-add-form {
      display: flex;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
    }

    .whitelist-add-form wa-input {
      flex: 1;
      min-width: 200px;
    }

    .whitelist-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 0.5rem;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .whitelist-table th,
    .whitelist-table td {
      padding: 0.75rem 1rem;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    .whitelist-table th {
      background: #f8f9fa;
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #6c757d;
    }
    .users-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 0.5rem;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .users-table th,
    .users-table td {
      padding: 0.75rem 1rem;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
    }

    .users-table th {
      background: #f8f9fa;
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #6c757d;
    }

    .users-table tr:hover {
      background: #f8f9fa;
    }

    .users-table tr.deleted {
      opacity: 0.5;
      background: #fff5f5;
    }

    .user-name {
      font-weight: 500;
    }

    .user-email {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .roles {
      display: flex;
      gap: 0.25rem;
      flex-wrap: wrap;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
    }

    .empty-state {
      text-align: center;
      padding: 3rem;
      color: #6c757d;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    .access-denied {
      text-align: center;
      padding: 3rem;
    }

    .access-denied wa-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    .dialog-content {
      padding: 1rem 0;
    }

    .role-checkboxes {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin: 1rem 0;
    }

    .stats-row {
      display: flex;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
      flex-wrap: wrap;
    }

    .stat-card {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem 1.5rem;
      min-width: 120px;
    }

    .stat-value {
      font-size: 1.5rem;
      font-weight: 600;
      color: #333;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #6c757d;
    }

    @media (max-width: 768px) {
      .users-table {
        display: block;
        overflow-x: auto;
      }

      .toolbar {
        flex-direction: column;
      }

      .search-box {
        width: 100%;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();

    // Check if user is admin
    if (!authService.state.isAdmin) {
      return;
    }

    this.loadUsers();
    this.loadWhitelist();
    this.loadClinicGroups();
  }

  private async loadClinicGroups() {
    this.clinicsLoading = true;
    try {
      this.clinicGroups = await adminService.getClinicGroups();
    } catch (err) {
      console.error("Failed to load clinic groups:", err);
    } finally {
      this.clinicsLoading = false;
    }
  }

  private async loadWhitelist() {
    this.whitelistLoading = true;
    try {
      this.whitelist = await adminService.getWhitelist();
    } catch (err) {
      console.error("Failed to load whitelist:", err);
    } finally {
      this.whitelistLoading = false;
    }
  }

  private async loadUsers() {
    this.isLoading = true;
    this.error = "";

    try {
      this.users = await adminService.getUsers();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load users";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    // Check admin access
    if (!authService.state.isAdmin) {
      return this.renderAccessDenied();
    }

    return html`
      <app-shell pageTitle="Admin">
        <div class="admin-content">
          <h1>
            <wa-icon
              name="shield-lock"
              style="vertical-align: middle;"
            ></wa-icon>
            Admin Panel
          </h1>
          <p class="subtitle">Manage users and system settings</p>

          ${this.error
            ? html`<wa-callout variant="danger" open>${this.error}</wa-callout>`
            : ""}

          <wa-tab-group
            @wa-tab-show=${(e: CustomEvent) => (this.activeTab = e.detail.name)}
          >
            <wa-tab
              slot="nav"
              panel="users"
              ?active=${this.activeTab === "users"}
            >
              <wa-icon name="users"></wa-icon>
              Users
            </wa-tab>
            <wa-tab
              slot="nav"
              panel="clinics"
              ?active=${this.activeTab === "clinics"}
            >
              <wa-icon name="hospital"></wa-icon>
              Clinics
            </wa-tab>
            <wa-tab
              slot="nav"
              panel="whitelist"
              ?active=${this.activeTab === "whitelist"}
            >
              <wa-icon name="shield-check"></wa-icon>
              Whitelist (Beta)
            </wa-tab>
            <wa-tab
              slot="nav"
              panel="debug"
              ?active=${this.activeTab === "debug"}
            >
              <wa-icon name="bug"></wa-icon>
              Debug Logs
            </wa-tab>

            <wa-tab-panel name="users" class="tab-content">
              ${this.isLoading
                ? this.renderLoading()
                : html`
                    ${this.renderStats()} ${this.renderToolbar()}
                    ${this.renderUsersTable()}
                  `}
            </wa-tab-panel>

            <wa-tab-panel name="clinics" class="tab-content">
              ${this.renderClinicsPanel()}
            </wa-tab-panel>

            <wa-tab-panel name="whitelist" class="tab-content">
              ${this.renderWhitelistPanel()}
            </wa-tab-panel>

            <wa-tab-panel name="debug" class="tab-content">
              <div style="text-align: center; padding: 2rem;">
                <wa-icon
                  name="bug"
                  style="font-size: 3rem; color: #6c757d; margin-bottom: 1rem;"
                ></wa-icon>
                <h3>Debug Reports</h3>
                <p style="color: #6c757d; margin-bottom: 1.5rem;">
                  View and manage user-submitted debug reports for
                  troubleshooting.
                </p>
                <wa-button
                  variant="brand"
                  @click=${() => Router.go("/admin/debug-logs")}
                >
                  <wa-icon slot="prefix" name="arrow-right"></wa-icon>
                  View Debug Logs
                </wa-button>
              </div>
            </wa-tab-panel>
          </wa-tab-group>

          ${this.renderEditDialog()}
        </div>
      </app-shell>
    `;
  }

  private renderAccessDenied() {
    return html`
      <div class="access-denied">
        <wa-icon name="shield-xmark"></wa-icon>
        <h2>Access Denied</h2>
        <p>You don't have permission to access the admin panel.</p>
        <wa-button variant="brand" @click=${() => Router.go("/")}>
          Go to Home
        </wa-button>
      </div>
    `;
  }

  private renderLoading() {
    return html`
      <div class="spinner-container">
        <wa-spinner style="font-size: 2rem;"></wa-spinner>
      </div>
    `;
  }

  private renderStats() {
    const totalUsers = this.users.length;
    const activeUsers = this.users.filter((u) => !u.isDeleted).length;
    const adminUsers = this.users.filter((u) => u.isAdmin).length;
    const diabeticUsers = this.users.filter((u) =>
      u.roles.includes("diabetic"),
    ).length;
    const monitorUsers = this.users.filter((u) =>
      u.roles.includes("monitor"),
    ).length;
    const clinicianUsers = this.users.filter((u) =>
      u.roles.includes("clinician"),
    ).length;

    return html`
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-value">${totalUsers}</div>
          <div class="stat-label">Total Users</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${activeUsers}</div>
          <div class="stat-label">Active</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${adminUsers}</div>
          <div class="stat-label">Admins</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${diabeticUsers}</div>
          <div class="stat-label">Diabetics</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${monitorUsers}</div>
          <div class="stat-label">Monitors</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${clinicianUsers}</div>
          <div class="stat-label">Clinicians</div>
        </div>
      </div>
    `;
  }

  private renderToolbar() {
    return html`
      <div class="toolbar">
        <wa-input
          class="search-box"
          placeholder="Search users..."
          clearable
          @input=${(e: Event) => {
            const input = e.target as any;
            this.searchQuery = input.value;
          }}
        >
          <wa-icon slot="prefix" name="magnifying-glass"></wa-icon>
        </wa-input>
        <wa-select
          class="role-filter"
          value=${this.roleFilter}
          @change=${(e: Event) =>
            (this.roleFilter = (e.target as HTMLSelectElement).value)}
        >
          <wa-option value="all">All Roles</wa-option>
          <wa-option value="diabetic">Diabetics</wa-option>
          <wa-option value="monitor">Monitors</wa-option>
          <wa-option value="clinician">Clinicians</wa-option>
          <wa-option value="admin">Admins</wa-option>
        </wa-select>
        <wa-checkbox
          ?checked=${this.showDeletedUsers}
          @change=${(e: Event) =>
            (this.showDeletedUsers = (e.target as HTMLInputElement).checked)}
        >
          Show deleted users
        </wa-checkbox>
        <wa-button @click=${() => this.loadUsers()}>
          <wa-icon slot="prefix" name="arrows-rotate"></wa-icon>
          Refresh
        </wa-button>
      </div>
    `;
  }

  private renderUsersTable() {
    let filteredUsers = this.users;

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filteredUsers = filteredUsers.filter(
        (u) =>
          u.displayName.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query),
      );
    }

    // Filter by role
    if (this.roleFilter !== "all") {
      if (this.roleFilter === "admin") {
        filteredUsers = filteredUsers.filter((u) => u.isAdmin);
      } else {
        filteredUsers = filteredUsers.filter((u) =>
          u.roles.includes(this.roleFilter),
        );
      }
    }

    // Filter deleted users
    if (!this.showDeletedUsers) {
      filteredUsers = filteredUsers.filter((u) => !u.isDeleted);
    }

    if (filteredUsers.length === 0) {
      return html`
        <div class="empty-state">
          <p>No users found</p>
        </div>
      `;
    }

    return html`
      <table class="users-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Roles</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${filteredUsers.map((user) => this.renderUserRow(user))}
        </tbody>
      </table>
    `;
  }

  private renderUserRow(user: UserSummary) {
    const currentUserId = authService.state.userId;
    const isCurrentUser = user.userId === currentUserId;

    return html`
      <tr class="${user.isDeleted ? "deleted" : ""}">
        <td>
          <div class="user-name">
            ${user.displayName}
            ${user.isAdmin
              ? html`<wa-badge variant="warning" style="margin-left: 0.5rem;"
                  >Admin</wa-badge
                >`
              : ""}
            ${isCurrentUser
              ? html`<wa-badge variant="neutral" style="margin-left: 0.5rem;"
                  >You</wa-badge
                >`
              : ""}
          </div>
          <div class="user-email">${user.email}</div>
        </td>
        <td>
          <div class="roles">
            ${user.roles.map(
              (role) => html`
                <wa-badge
                  variant="${role === "diabetic"
                    ? "brand"
                    : role === "monitor"
                      ? "success"
                      : "neutral"}"
                >
                  ${role}
                </wa-badge>
              `,
            )}
          </div>
        </td>
        <td>
          ${user.isDeleted
            ? html`<wa-badge variant="danger">Deleted</wa-badge>`
            : html`<wa-badge variant="success">Active</wa-badge>`}
        </td>
        <td>${new Date(user.createdAt).toLocaleDateString()}</td>
        <td>
          <div class="actions">
            <wa-button size="small" @click=${() => this.editUser(user)}>
              <wa-icon name="pen"></wa-icon>
            </wa-button>
            ${!isCurrentUser
              ? html`
                  ${user.isDeleted
                    ? html`
                        <wa-button
                          size="small"
                          @click=${() => this.restoreUser(user)}
                        >
                          <wa-icon name="rotate-left"></wa-icon>
                        </wa-button>
                      `
                    : html`
                        <wa-button
                          size="small"
                          variant="danger"
                          @click=${() => this.deleteUser(user)}
                        >
                          <wa-icon name="trash"></wa-icon>
                        </wa-button>
                      `}
                `
              : ""}
          </div>
        </td>
      </tr>
    `;
  }

  private renderEditDialog() {
    if (!this.editingUser) return "";

    const currentUserId = authService.state.userId;
    const isCurrentUser = this.editingUser.userId === currentUserId;

    return html`
      <wa-dialog
        label="Edit User - ${this.editingUser.displayName}"
        ?open=${!!this.editingUser}
        @wa-hide=${() => (this.editingUser = null)}
      >
        <div class="dialog-content">
          <p><strong>Email:</strong> ${this.editingUser.email}</p>

          <h4>Roles</h4>
          <div class="role-checkboxes">
            <wa-checkbox
              ?checked=${this.editRoles.has("diabetic")}
              @change=${(e: Event) =>
                this.toggleRole(
                  "diabetic",
                  (e.target as HTMLInputElement).checked,
                )}
            >
              Diabetic (person with diabetes)
            </wa-checkbox>
            <wa-checkbox
              ?checked=${this.editRoles.has("monitor")}
              @change=${(e: Event) =>
                this.toggleRole(
                  "monitor",
                  (e.target as HTMLInputElement).checked,
                )}
            >
              Monitor (family/friend)
            </wa-checkbox>
            <wa-checkbox
              ?checked=${this.editRoles.has("clinician")}
              @change=${(e: Event) =>
                this.toggleRole(
                  "clinician",
                  (e.target as HTMLInputElement).checked,
                )}
            >
              Clinician (healthcare professional)
            </wa-checkbox>
          </div>

          ${!isCurrentUser
            ? html`
                <h4>Admin Status</h4>
                <wa-checkbox
                  ?checked=${this.editingUser.isAdmin}
                  @change=${(e: Event) =>
                    this.toggleAdmin((e.target as HTMLInputElement).checked)}
                >
                  Administrator
                </wa-checkbox>
              `
            : ""}
        </div>

        <wa-button slot="footer" @click=${() => (this.editingUser = null)}>
          Cancel
        </wa-button>
        <wa-button
          slot="footer"
          variant="brand"
          @click=${() => this.saveUser()}
        >
          Save Changes
        </wa-button>
      </wa-dialog>
    `;
  }

  private editUser(user: UserSummary) {
    this.editingUser = user;
    this.editRoles = new Set(user.roles);
  }

  // =========================================================================
  // Clinics Panel
  // =========================================================================

  private renderClinicsPanel() {
    if (this.clinicsLoading) {
      return this.renderLoading();
    }

    return html`
      <div class="whitelist-section">
        <div class="whitelist-header">
          <div>
            <h3 style="margin: 0;">Clinic Groups</h3>
            <p style="color: #6c757d; margin: 0.5rem 0 0 0;">
              Organize clinicians by their clinic or practice
            </p>
          </div>
        </div>
      </div>

      <h4>Add Clinic Group</h4>
      <div class="whitelist-add-form">
        <wa-input
          placeholder="Clinic name"
          .value=${this.newClinicName}
          @input=${(e: Event) => {
            const input = e.target as any;
            this.newClinicName = input.value;
          }}
        >
          <wa-icon slot="prefix" name="hospital"></wa-icon>
        </wa-input>
        <wa-input
          placeholder="Description (optional)"
          .value=${this.newClinicDescription}
          @input=${(e: Event) => {
            const input = e.target as any;
            this.newClinicDescription = input.value;
          }}
        >
          <wa-icon slot="prefix" name="id-card"></wa-icon>
        </wa-input>
        <wa-button variant="brand" @click=${() => this.addClinicGroup()}>
          <wa-icon slot="prefix" name="plus"></wa-icon>
          Add
        </wa-button>
      </div>

      ${this.renderClinicsTable()}
    `;
  }

  private renderClinicsTable() {
    if (this.clinicGroups.length === 0) {
      return html`
        <div class="empty-state">
          <p>No clinic groups created yet</p>
        </div>
      `;
    }

    return html`
      <table class="whitelist-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.clinicGroups.map(
            (clinic) => html`
              <tr>
                <td style="font-weight: 500;">${clinic.name}</td>
                <td>${clinic.description || "-"}</td>
                <td>
                  <wa-badge variant=${clinic.isActive ? "success" : "neutral"}>
                    ${clinic.isActive ? "Active" : "Inactive"}
                  </wa-badge>
                </td>
                <td>${new Date(clinic.createdAt).toLocaleDateString()}</td>
                <td class="actions">
                  <wa-button
                    size="small"
                    variant="danger"
                    @click=${() => this.deleteClinicGroup(clinic.id)}
                  >
                    <wa-icon name="trash"></wa-icon>
                  </wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  private async addClinicGroup() {
    if (!this.newClinicName) return;

    try {
      await adminService.createClinicGroup(
        this.newClinicName,
        this.newClinicDescription || undefined,
      );
      this.newClinicName = "";
      this.newClinicDescription = "";
      await this.loadClinicGroups();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to create clinic group";
    }
  }

  private async deleteClinicGroup(id: string) {
    if (!confirm("Delete this clinic group?")) return;

    try {
      await adminService.deleteClinicGroup(id);
      await this.loadClinicGroups();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to delete clinic group";
    }
  }

  // =========================================================================
  // Whitelist Panel
  // =========================================================================

  private renderWhitelistPanel() {
    if (this.whitelistLoading) {
      return this.renderLoading();
    }

    return html`
      <div class="whitelist-section">
        <div class="whitelist-header">
          <div>
            <h3 style="margin: 0;">Registration Whitelist</h3>
            <p style="color: #6c757d; margin: 0.5rem 0 0 0;">
              When enabled, only whitelisted emails can register
            </p>
          </div>
          <wa-switch
            ?checked=${this.whitelist?.enabled ?? false}
            @change=${(e: Event) =>
              this.toggleWhitelistEnabled(
                (e.target as HTMLInputElement).checked,
              )}
          >
            ${this.whitelist?.enabled ? "Enabled" : "Disabled"}
          </wa-switch>
        </div>
      </div>

      <h4>Add Email to Whitelist</h4>
      <div class="whitelist-add-form">
        <wa-input
          placeholder="Email address"
          type="email"
          .value=${this.newWhitelistEmail}
          @input=${(e: Event) => {
            const input = e.target as any;
            this.newWhitelistEmail = input.value;
          }}
        >
          <wa-icon slot="prefix" name="envelope"></wa-icon>
        </wa-input>
        <wa-input
          placeholder="Note (optional)"
          .value=${this.newWhitelistNote}
          @input=${(e: Event) => {
            const input = e.target as any;
            this.newWhitelistNote = input.value;
          }}
        >
          <wa-icon slot="prefix" name="note-sticky"></wa-icon>
        </wa-input>
      </div>
      <div class="whitelist-add-form" style="margin-top: -0.5rem;">
        <wa-select
          placeholder="Assign to clinic (optional)"
          clearable
          .value=${this.newWhitelistClinicGroupId}
          @change=${(e: Event) =>
            (this.newWhitelistClinicGroupId = (
              e.target as HTMLSelectElement
            ).value)}
        >
          <wa-icon slot="prefix" name="hospital"></wa-icon>
          ${this.clinicGroups
            .filter((c) => c.isActive)
            .map(
              (clinic) => html`
                <wa-option value=${clinic.id}>${clinic.name}</wa-option>
              `,
            )}
        </wa-select>
        <wa-select
          placeholder="Pre-assign role (optional)"
          clearable
          .value=${this.newWhitelistRole}
          @change=${(e: Event) =>
            (this.newWhitelistRole = (e.target as HTMLSelectElement).value)}
        >
          <wa-icon slot="prefix" name="id-badge"></wa-icon>
          <wa-option value="diabetic">Diabetic</wa-option>
          <wa-option value="monitor">Monitor</wa-option>
          <wa-option value="clinician">Clinician</wa-option>
        </wa-select>
        <wa-button variant="brand" @click=${() => this.addWhitelistEmail()}>
          <wa-icon slot="prefix" name="plus"></wa-icon>
          Add
        </wa-button>
      </div>

      ${this.renderWhitelistTable()}
    `;
  }

  private renderWhitelistTable() {
    const emails = this.whitelist?.emails ?? [];

    if (emails.length === 0) {
      return html`
        <div class="empty-state">
          <p>No whitelisted emails yet</p>
        </div>
      `;
    }

    // Helper to get clinic name by ID
    const getClinicName = (clinicId?: string) => {
      if (!clinicId) return "-";
      const clinic = this.clinicGroups.find((c) => c.id === clinicId);
      return clinic?.name || clinicId;
    };

    return html`
      <table class="whitelist-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Clinic</th>
            <th>Role</th>
            <th>Note</th>
            <th>Added By</th>
            <th>Added</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${emails.map(
            (entry) => html`
              <tr>
                <td>${entry.email}</td>
                <td>${getClinicName(entry.clinicGroupId)}</td>
                <td>
                  ${entry.preAssignedRole
                    ? html`<wa-badge variant="brand"
                        >${entry.preAssignedRole}</wa-badge
                      >`
                    : "-"}
                </td>
                <td>${entry.note || "-"}</td>
                <td>${entry.addedBy || "-"}</td>
                <td>${new Date(entry.addedAt).toLocaleDateString()}</td>
                <td>
                  <wa-button
                    size="small"
                    variant="danger"
                    @click=${() => this.removeWhitelistEmail(entry.email)}
                  >
                    <wa-icon name="trash"></wa-icon>
                  </wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  private async toggleWhitelistEnabled(enabled: boolean) {
    try {
      await adminService.setWhitelistEnabled(enabled);
      await this.loadWhitelist();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to update whitelist";
    }
  }

  private async addWhitelistEmail() {
    if (!this.newWhitelistEmail) return;

    try {
      await adminService.addWhitelistEmail(
        this.newWhitelistEmail,
        this.newWhitelistNote || undefined,
        this.newWhitelistClinicGroupId || undefined,
        this.newWhitelistRole || undefined,
      );
      this.newWhitelistEmail = "";
      this.newWhitelistNote = "";
      this.newWhitelistClinicGroupId = "";
      this.newWhitelistRole = "";
      await this.loadWhitelist();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to add email to whitelist";
    }
  }

  private async removeWhitelistEmail(email: string) {
    if (!confirm(`Remove ${email} from whitelist?`)) return;

    try {
      await adminService.removeWhitelistEmail(email);
      await this.loadWhitelist();
    } catch (err) {
      this.error =
        err instanceof Error
          ? err.message
          : "Failed to remove email from whitelist";
    }
  }

  private toggleRole(role: string, checked: boolean) {
    const newRoles = new Set(this.editRoles);
    if (checked) {
      newRoles.add(role);
    } else {
      newRoles.delete(role);
    }
    this.editRoles = newRoles;
  }

  private async toggleAdmin(isAdmin: boolean) {
    if (!this.editingUser) return;

    try {
      await adminService.setAdminStatus(this.editingUser.userId, isAdmin);
      this.editingUser = { ...this.editingUser, isAdmin };
      await this.loadUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to update admin status";
    }
  }

  private async saveUser() {
    if (!this.editingUser) return;

    try {
      await adminService.setUserRoles(
        this.editingUser.userId,
        Array.from(this.editRoles),
      );
      this.editingUser = null;
      await this.loadUsers();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to save user";
    }
  }

  private async deleteUser(user: UserSummary) {
    if (!confirm(`Delete user ${user.displayName}?`)) return;

    try {
      await adminService.deleteUser(user.userId);
      await this.loadUsers();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to delete user";
    }
  }

  private async restoreUser(user: UserSummary) {
    try {
      await adminService.restoreUser(user.userId);
      await this.loadUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to restore user";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "admin-page": AdminPage;
  }
}
